import { authHeaders } from "@/utils/authHeaders";
import { dashboardApiCalls, commonApiCalls } from "@/services/api";
import { IS_DEV, NEXT_PUBLIC_ACCESS_TOKEN } from "@/config/env";

const getFetchInfo = async () => {
  if (IS_DEV) {
    return {
      token: process.env.NEXT_PUBLIC_ACCESS_TOKEN,
      headers: authHeaders,
    };
  }

  const response = await fetch(commonApiCalls.client.authorizer, {
    headers: {
      ...authHeaders,
    },
  });
  const { token } = await response.json();

  if (IS_DEV) {
    return {
      token: NEXT_PUBLIC_ACCESS_TOKEN,
      headers: token.headers,
    };
  }

  return {
    token: token.access,
    headers: token.headers,
  };
};

export const createStudents = async (companyId: "", students: Array<Record<string, any>>) => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.createStudents(companyId), {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(students),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::createStudents", error);
    throw new Error("::ERROR::createStudents", { cause: error });
  }
};

export const getNewStudentsPage = async (companyId = "", page = 1): Promise<StudentLicenseInfo[]> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.studentsByPage(companyId, page), {
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::createStudents", error);
    throw new Error("::ERROR::createStudents", { cause: error });
  }
};

export const renewStudents = async (companyId: string | number, students: any) => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.renewStudents(companyId), {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(students),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::renewStudents", error);
    throw new Error("::ERROR::renewStudents", { cause: error });
  }
};

export const getStudentsLicenses = async (companyId: string): Promise<StudentsInfoType> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.students(companyId), {
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
    });

    const { data, metadata } = await res.json();
    return { data, metadata };
  } catch (error) {
    console.error("::ERROR::getStudentsLicenses", error);
    throw new Error("::ERROR::getStudentsLicenses", { cause: error });
  }
};

export const deleteStudents = async (
  companyId: string,
  students: Array<Record<string, any>>
): Promise<StudentLicenseInfo[]> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.students(companyId), {
      method: "DELETE",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(students),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::deleteStudents", error);
    throw new Error("::ERROR::deleteStudents", { cause: error });
  }
};

export const getIsStudentActive = async (companyId: string, email: string): Promise<StudentLicenseInfo[]> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.permission.isUserActive(companyId, email), {
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::getIsStudentActive", error);
    throw new Error("::ERROR::getIsStudentActive", { cause: error });
  }
};

export const assignPermission = async (companyId: string, student: Array<Record<string, any>>) => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.permission.assignPermission(companyId, student), {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(student),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::assignPermission", error);
    throw new Error("::ERROR::assignPermission", { cause: error });
  }
};

export const getSearchStudents = async (companyId: string, searchTerm: string): Promise<StudentLicenseInfo[]> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.permission.searchUsers(companyId, searchTerm), {
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::getSearchStudents", error);
    throw new Error("::ERROR::getSearchStudents", { cause: error });
  }
};

export const getFilteredStudents = async (companyId: string, queryParams: string): Promise<StudentsInfoType> => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.filteredStudents(companyId, queryParams), {
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
    });

    const { data, metadata } = await res.json();
    return { data, metadata };
  } catch (error) {
    console.error("::ERROR::getFilteredStudents", error);
    throw new Error("::ERROR::getFilteredStudents", { cause: error });
  }
};

export const resolveExtensions = async (resolve: ResolveType) => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.extensions.resolveExtensions(), {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(resolve),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::resolveExtensions", error);
    throw new Error("::ERROR::resolveExtensions", { cause: error });
  }
};

export const bulkMonthsAssign = async (companyId: number, payload: any) => {
  try {
    const { token } = await getFetchInfo();
    const res = await fetch(dashboardApiCalls.licenses.bulkMonthsAssign(companyId), {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "API-Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify(payload),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::bulkMonthsAssign", error);
    throw new Error("::ERROR::bulkMonthsAssign", { cause: error });
  }
};

let coursesTakenQuery = `query studentStatsReport($user_ids: [Int]!) {
  studentStatsReport(userIds: $user_ids) {
    userId
    summary {
      userId
      activeCourses
      approvedCourses
      logDate
    }
  }
}`;

export const coursesTakenByStudents = async (userIds: Array<string | null>, companyId: string) => {
  let variables = {
    user_ids: userIds,
  };

  if (companyId !== "") {
    Object.assign(variables, { companyId: parseInt(companyId) });

    coursesTakenQuery = `query studentStatsReport($user_ids: [Int]!, $companyId: Int!) {
      studentStatsReport(userIds: $user_ids, companyId: $companyId) {
        userId
        summary {
          userId
          activeCourses
          approvedCourses
          logDate
        }
      }
    }`;
  }

  try {
    const { token } = await getFetchInfo();

    const res = await fetch(dashboardApiCalls.licenses.coursesTakenByStudents, {
      method: "POST",
      headers: new Headers({
        ...authHeaders,
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${token}`,
      }),
      body: JSON.stringify({ query: coursesTakenQuery, variables }),
    });

    const { data } = await res.json();
    return data;
  } catch (error) {
    console.error("::ERROR::coursesTakenByStudents", error);
    throw new Error("::ERROR::coursesTakenByStudents", { cause: error });
  }
};
